// ÚNICA FUENTE DE VERDAD — cómo se interpreta fit_mode + transform de un hueco al
// dibujar su foto. La usan TODOS los renderizadores del DOM (lienzo del editor y
// miniatura del navegador de lienzos): mismo FIT/CONTAIN (foto completa, centrada,
// proporción original), mismo COVER ("fill": rellena y recorta sin deformar) y la
// misma transformación virtual (escala + offsets). Cada renderizador solo cambia la
// resolución a la que dibuja: en el lienzo los offsets se escalan con ppm (mm→px) y
// en la miniatura se expresan como porcentaje del hueco — la GEOMETRÍA en mm es
// idéntica en ambos. La exportación (spreadRenderer) implementa la misma matemática
// en canvas 2D. Ningún componente puede volver a interpretar el ajuste por su cuenta.

export function slotPhotoView(slot) {
  const t = (slot && slot.transform) || {};
  const fx = typeof t.focus_x === "number" ? t.focus_x : 0.5;
  const fy = typeof t.focus_y === "number" ? t.focus_y : 0.5;
  return {
    objectFit: slot?.fit_mode === "fill" ? "cover" : "contain",
    // Encuadre del cover hacia el sujeto (caras). En "fit" es irrelevante (la foto cabe
    // entera), pero centrar es lo correcto.
    objectPosition: slot?.fit_mode === "fill" ? `${fx * 100}% ${fy * 100}%` : "50% 50%",
    scale: t.scale ?? 1,
    // Offsets normalizados al tamaño del hueco (porcentaje): equivalen exactamente a
    // offset_mm * ppm en el lienzo, porque el <img> ocupa el 100 % del hueco.
    offsetXPct: slot?.w_mm > 0 ? ((t.offset_x_mm || 0) / slot.w_mm) * 100 : 0,
    offsetYPct: slot?.h_mm > 0 ? ((t.offset_y_mm || 0) / slot.h_mm) * 100 : 0,
  };
}

// Aviso de calidad — resolución EFECTIVA (ppp) con la que la foto se imprimiría en
// su hueco según su tamaño NATIVO (width_px/height_px), su ajuste (contain/cover) y
// el zoom virtual (transform.scale). Se evalúa el EJE PEOR (ppp mínimos). null si
// la foto no tiene dimensiones nativas registradas.
export function slotEffDpi(slot, photo) {
  const wp = photo?.width_px;
  const hp = photo?.height_px;
  if (!(wp > 0) || !(hp > 0)) return null;
  const sw = slot?.w_mm;
  const sh = slot?.h_mm;
  if (!(sw > 0) || !(sh > 0)) return null;
  const r = wp / hp;
  const s = slot?.transform?.scale ?? 1;
  const base = slot?.fit_mode === "fill"
    ? { w: Math.max(sw, sh * r), h: Math.max(sh, sw / r) }
    : { w: Math.min(sw, sh * r), h: Math.min(sh, sw / r) };
  const dw = base.w * s;
  const dh = base.h * s;
  if (dw <= 0 || dh <= 0) return null;
  return Math.min((25.4 * wp) / dw, (25.4 * hp) / dh);
}

// Reencuadre en modo RELLENO (cover): margen máximo (mm) que la foto puede
// desplazarse en cada eje SIN dejar de cubrir el contenedor (jamás huecos).
// Es la misma matemática de cobertura que smartFillTransform: (k·d − lado) / 2.
// null si la foto o el hueco no tienen geometría utilizable.
export function coverPanMargins(slot, photo) {
  const wp = photo?.width_px;
  const hp = photo?.height_px;
  if (!(wp > 0) || !(hp > 0)) return null;
  const sw = slot?.w_mm;
  const sh = slot?.h_mm;
  if (!(sw > 0) || !(sh > 0)) return null;
  const r = wp / hp;
  const dw = Math.max(sw, sh * r);
  const dh = Math.max(sh, sw / r);
  const k = slot?.transform?.scale ?? 1;
  return { mx: Math.max(0, (k * dw - sw) / 2), my: Math.max(0, (k * dh - sh) / 2) };
}

// ZOOM MÍNIMO — en modo RELLENO (cover) el mínimo es 1: la foto SIEMPRE cubre el
// contenedor por completo (alejar por debajo dejaría franjas vacías con object-fit:
// cover). Para reencuadrar se AMPLÍA el zoom (k>1) y se arrastra. En modo FIT (foto
// completa) se permite alejar hasta 0.3 (la foto ya cabe entera, sin recorte).
export function minZoomForFullOriginal(slot) {
  return slot?.fit_mode === "fill" ? 1 : 0.3;
}

// REENCUADRE NO DESTRUCTIVO — márgenes de arrastre de la FOTO dentro del hueco (modo
// relleno), POR EJE, CONSISTENTES con el render real (object-fit:cover + scale(k) +
// translate). A escala 1 la foto ya CUBRE el hueco exactamente, así que el margen es 0
// (arrastrar no la mueve: nunca aparecen franjas). Al ampliar el zoom (k>1) la imagen
// ampliada sobresale (k−1)/2·lado por cada lado, y ESE es el recorrido de arrastre
// disponible sin descubrir el fondo. Nunca devuelve null en modo relleno: el eje
// SIEMPRE se limita (jamás libre) para que el contenedor quede siempre cubierto.
export function photoPanMargins(slot) {
  const sw = slot?.w_mm;
  const sh = slot?.h_mm;
  if (!(sw > 0) || !(sh > 0)) return null;
  const k = slot?.transform?.scale ?? 1;
  return { mx: Math.max(0, ((k - 1) * sw) / 2), my: Math.max(0, ((k - 1) * sh) / 2) };
}